import { Link } from "react-router-dom";
import {
    CreditCard, Receipt, FileText, Banknote, AlertTriangle,
    ClipboardCheck, ShieldCheck, HelpCircle, ArrowRight, Sparkles, Mail,
} from "lucide-react";

const SECTIONS = [
    {
        icon: CreditCard,
        title: "How to add a card",
        steps: [
            "Open Credit Cards → Add New Card.",
            "Fill Bank, Card Name, Last 4 digits, Holder, Limit, Statement day, Due day.",
            "Never enter the full 16-digit card number — the system stores only the last 4.",
            "Optional: set a statement PDF password (encrypted; reveal is role-gated + audit-logged).",
            "Save. The card now appears in Card Master with a health badge.",
        ],
        cta: { to: "/cards/new", label: "Add a card" },
    },
    {
        icon: Receipt,
        title: "How to post an expense",
        steps: [
            "Open Expenses → Add Expense (or use Quick Entry on mobile).",
            "Pick the card. Bank + last-4 auto-fill from the master.",
            "Enter Gross Amount → Coins/Points/Cashback/Discounts → Final Amount Charged. Only the final amount affects card outstanding.",
            "Tag Business or Personal. Attach receipt if available.",
            "Save. If approval is required, the expense goes to Pending Approval.",
        ],
        cta: { to: "/quick-entry", label: "Quick entry" },
    },
    {
        icon: FileText,
        title: "How to upload a statement",
        steps: [
            "Open Statements → Upload Statement.",
            "Pick the card and statement month. Attach the PDF.",
            "OCR pre-fills totals, due date, and transactions — review, edit if needed, save.",
            "The dashboard clears the \"Statement missing\" alert automatically.",
        ],
        cta: { to: "/statements", label: "Open statements" },
    },
    {
        icon: Banknote,
        title: "How to record a payment",
        steps: [
            "Open Payments → Record Payment.",
            "Pick the card. Enter amount, mode (UPI/Bank Transfer/CRED/Amazon Pay/Auto Debit/Other).",
            "Add UTR + upload proof if available.",
            "Save. Once the card account shows it, mark \"Reflected\" — the alert clears.",
        ],
        cta: { to: "/payments", label: "Open payments" },
    },
    {
        icon: AlertTriangle,
        title: "How to check pending actions",
        steps: [
            "Open Home — the \"What Needs Attention Today\" list shows only what needs action.",
            "Every item has a priority pill (Critical / High / Medium / Low) and a one-click action.",
            "For the full view, open Finance Action Center — buckets covering overdue, due-soon, missing statements, unreflected payments, unapproved expenses, high utilization, missing receipts.",
        ],
        cta: { to: "/finance-review", label: "Finance Action Center" },
    },
    {
        icon: ClipboardCheck,
        title: "How to close a month",
        steps: [
            "Open Monthly Closing → pick the month.",
            "Complete the 12-item checklist (statements uploaded, expenses entered, reconciled, payments made & reflected, late fees checked, GST checked, reports exported…).",
            "Click \"Close month · Email PDF\". Books are locked; the branded Monthly Expense PDF is auto-emailed to the finance team.",
            "Only Admin can reopen a closed month.",
        ],
        cta: { to: "/monthly-closing", label: "Monthly closing" },
    },
    {
        icon: Mail,
        title: "How to enable Gmail auto-fetch",
        steps: [
            "In Google Cloud Console, create a project → enable the Gmail API → configure the OAuth consent screen (External, add yourself as a test user).",
            "Under Credentials, create an OAuth Client (Web application). Add the exact redirect URI shown on the Statements page banner: {FRONTEND_URL}/api/oauth/gmail/callback.",
            "Copy the Client ID and Client Secret and give them to your admin — they'll set GMAIL_OAUTH_CLIENT_ID and GMAIL_OAUTH_CLIENT_SECRET on the server.",
            "On the Statements page, click 'Connect Gmail' → grant read-only access. The dashboard stores only the refresh token; it never reads anything else.",
            "Click 'Sync last 45 days' to scan the inbox for each active card's 'Statement email from' + 'Subject' pattern. Every match lands in a review queue for you to accept or reject before it enters the ledger.",
        ],
        cta: { to: "/statements", label: "Open Statements" },
    },
    {
        icon: ShieldCheck,
        title: "What data is NOT stored (for security)",
        steps: [
            "CVV — never captured, never stored.",
            "OTPs — never asked for.",
            "Card PINs — never asked for.",
            "Net-banking / login passwords — never stored.",
            "Full 16-digit card numbers — only the last 4 digits are kept, everything else is masked.",
            "Statement PDF passwords are encrypted at rest; reveal is role-gated and audit-logged.",
        ],
        cta: { to: "/security", label: "Security Center" },
    },
];

export default function Help() {
    return (
        <div data-testid="help-page">
            <div className="mb-8">
                <div className="eyebrow">Help & Guide</div>
                <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                    Everything you need — in one page.
                </h1>
                <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                    Seven quick guides for daily use. If something isn&rsquo;t here, ask the
                    Finance Copilot — it&rsquo;s trained on this dashboard.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {SECTIONS.map((s) => (
                    <div
                        key={s.title}
                        className="rounded-2xl border border-stone-200 bg-white p-6"
                        data-testid={`help-section-${s.title.replace(/\W+/g, "-").toLowerCase()}`}
                    >
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-9 h-9 rounded-lg bg-stone-900 text-white flex items-center justify-center shrink-0">
                                <s.icon size={16} />
                            </div>
                            <h2 className="font-display text-lg text-stone-900 leading-tight pt-1">
                                {s.title}
                            </h2>
                        </div>
                        <ol className="space-y-2 mb-5 pl-1">
                            {s.steps.map((step, i) => (
                                <li key={i} className="flex gap-2.5 text-sm text-stone-700 leading-relaxed">
                                    <span className="text-xs text-stone-400 tabular-nums mt-0.5">{i + 1}.</span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                        {s.cta && (
                            <Link
                                to={s.cta.to}
                                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-stone-900 border-b border-stone-900 pb-0.5 hover:gap-2 transition-all"
                            >
                                {s.cta.label} <ArrowRight size={11} />
                            </Link>
                        )}
                    </div>
                ))}
            </div>

            {/* Footer callout */}
            <div className="mt-10 p-6 rounded-2xl border border-stone-200 bg-gradient-to-br from-stone-50 to-white flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-900 text-white flex items-center justify-center shrink-0">
                    <Sparkles size={18} />
                </div>
                <div className="flex-1">
                    <div className="font-display text-lg text-stone-900">Prefer to just ask?</div>
                    <p className="text-sm text-stone-600 mt-1">
                        The Finance Copilot understands your cards, statements, dues, and audit
                        trail. Ask &ldquo;What&rsquo;s overdue?&rdquo; or &ldquo;Which card should
                        I use for a flight?&rdquo; — it answers with your data.
                    </p>
                    <Link
                        to="/copilot"
                        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-blue-900 border-b border-blue-900 pb-0.5 hover:gap-2 transition-all mt-3"
                        data-testid="help-open-copilot"
                    >
                        Open Finance Copilot <ArrowRight size={11} />
                    </Link>
                </div>
                <HelpCircle size={16} className="text-stone-400" />
            </div>
        </div>
    );
}
